import React, { useEffect, useState, useMemo, FC } from 'react';
import get from 'lodash/get';
import classNames from 'classnames';
import { useQuery } from '@apollo/client';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import WithEventRole from 'components/user/WithEventRole';
import EventTitle from 'components/events/Timeline/EventTitle';
import TimelineLayout from 'components/layouts/TimelineLayout';
import PageContentHeader from 'components/common/PageContentHeader';
import TimelineEventInfo from 'components/events/TimelineEventInfo';
import EventSections from 'components/events/Timeline/EventSections';
import SectionBlocks from 'components/sections/blocks/SectionBlocks';
import ExportMusicButtons from 'components/buttons/ExportMusicButtons';
import { SectionsFilterableContext } from 'components/events/Timeline/SectionsFilterContext';
import { TimelineContext, TypeTimelineContext } from 'components/events/Timeline/TimelineContext';

import { useModal } from 'vibo-ui/Modal';
import { onError } from 'graphql/helpers';
import { EVENT_TIMELINE_GQL } from './constants';
import { EVENT_PAGE_TAB_KEYS } from 'services/constants';
import { useEvent } from 'components/context/EventContext';
import { updateSectionIdFromRoute } from 'services/sections/helpers';

import { UPDATE_EVENT_SECTION } from 'graphql/mutations/events';
import { SECTIONS, SECTIONS_NO_IMAGES_ALLOWED } from 'graphql/queries/events';

import { EventUserType, Modals } from 'types/enums';
import { EventTimelineGqlOptions } from './interfaces';
import { SectionSettingsModalProps } from 'components/modals/SectionSettingsModal';

const EventTimeline: FC = () => {
  const [selectedSection, setSelectedSection] = useState<Nullable<EventSection>>(null);
  const [filter, setFilter] = useState<Nullable<SectionsFilter>>(null);

  const eventContext = useEvent();
  const { event, isCoverPhotoAllowed, isLoading, isEventDj } = eventContext;

  const location = useLocation();
  const navigate = useNavigate();
  const { id: eventId = '', sectionId = '', page } = useParams<keyof EventPageRouteParams>();

  const baseTimelineRoute: string = `/event/${eventId}/${EVENT_PAGE_TAB_KEYS.timeline}`;

  const { openModal } = useModal();

  const openSectionSettinsModal = (section: TimelineSection) =>
    openModal<SectionSettingsModalProps<TimelineSection>>({
      key: Modals.sectionSettings,
      props: {
        refetchQueries: ['sections'],
        section: section as TimelineSection,
        updateSectionMutation: UPDATE_EVENT_SECTION,
        variables: { eventId, sectionId: section._id },
        eventContext,
      },
    });

  const { data, loading: sectionsLoading, refetch } = useQuery<
    EventSectionsResponse,
    EventSectionsVariables
  >(isCoverPhotoAllowed ? SECTIONS : SECTIONS_NO_IMAGES_ALLOWED, {
    variables: {
      eventId,
    },
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    skip: isLoading,
    onError,
  });

  const sections: EventSection[] = useMemo(() => get(data, 'sections', []), [data]);

  const shouldRenderComponentsForSection: boolean = useMemo(
    () => !!selectedSection && !!sections.length,
    [selectedSection, sections.length]
  );

  const config: TypeTimelineContext<EventTimelineGqlOptions> = {
    variables: {
      eventId,
      sectionId,
    },
    settings: {
      canAnswer: isEventDj || !event?.isLocked,
    },
    ...EVENT_TIMELINE_GQL,
  };

  useEffect(() => {
    updateSectionIdFromRoute(selectedSection, setSelectedSection, sectionId, sections);
  }, [sections, sectionId, location.search]);

  useEffect(() => {
    if (selectedSection && selectedSection._id !== sectionId) {
      navigate({
        pathname: `${baseTimelineRoute}/${selectedSection._id}`,
        search: location.search,
      });
    }
  }, [selectedSection, page, location.search]);

  return !!event ? (
    <TimelineContext.Provider value={config}>
      <TimelineEventInfo />
      <TimelineLayout
        left={
          <span
            className={classNames('content-fade-in ', {
              'ready _1': (!!sections && !sectionsLoading) || !isLoading,
            })}
          >
            <SectionsFilterableContext.Provider
              value={{
                filter,
                setFilter,
              }}
            >
              <EventSections
                event={event}
                sections={sections}
                selectedSection={selectedSection}
                // @ts-ignore
                onSectionClick={setSelectedSection}
                onEditClick={section => {
                  openSectionSettinsModal(section);
                }}
                refetch={refetch}
                isSectionsLoading={sectionsLoading}
              />
            </SectionsFilterableContext.Provider>
          </span>
        }
        right={
          shouldRenderComponentsForSection && selectedSection ? (
            <SectionBlocks<EventSection>
              sections={sections}
              section={selectedSection}
              onEditClick={() => openSectionSettinsModal(selectedSection)}
            />
          ) : null
        }
      >
        <PageContentHeader>
          <EventTitle name={event.title} />
        </PageContentHeader>
      </TimelineLayout>
      <WithEventRole roles={[EventUserType.dj]}>
        <ExportMusicButtons />
      </WithEventRole>
    </TimelineContext.Provider>
  ) : null;
};

export default EventTimeline;
